import React, { useState, useEffect, useMemo, useRef, useCallback } from "react";
import ExportCSVModal from "../Modals/ExportCSVModal";
import ImportCSVModal from "../Modals/ImportCSVModal";
import {
  ChevronDownIcon,
  ChevronUpIcon,
  ChevronUpDownIcon,
  TrashIcon,
  DocumentDuplicateIcon,
  MagnifyingGlassIcon,
  XMarkIcon,
  FunnelIcon,
  ViewColumnsIcon,
  BookmarkSquareIcon,
  ArrowPathIcon,
  PlusIcon,
  CheckIcon,
} from "@heroicons/react/20/solid";
import {
  classNames,
  formatInput,
  waitForCookie,
} from "../../utils/helperFuctions";
import { config } from "../../utils/config";
import { getCookie, setCookie } from "../../utils/cookieHandler";
import { useLocation, useNavigate, useSearchParams } from "react-router-dom";
import axios from "axios";
import PaginationButtons from "./PaginationButtons";
import SearchableDropdownOptions from "./SearchableDropdownOptions";
import InputDateTimePickerMUI from "../InputFields/InputDatePicker/InputDateTimePickerMUI";
import SequenceConfirmationPopup from "../BillingEvents/SequenceConfirmationPopup";
import "../../pages/DashboardPage/DashboardPage.css";

const DENSITY_OPTIONS = {
  compact: {
    label: "Compact",
    cellPadding: "py-2 px-3",
    headerPadding: "py-2.5 px-3",
    textSize: "text-xs",
    badgePadding: "px-2 py-0.5 text-[11px]",
  },
  normal: {
    label: "Normal",
    cellPadding: "py-3 px-4",
    headerPadding: "py-3 px-4",
    textSize: "text-sm",
    badgePadding: "px-2.5 py-0.5 text-xs",
  },
  comfortable: {
    label: "Comfortable",
    cellPadding: "py-4 px-4",
    headerPadding: "py-3.5 px-4",
    textSize: "text-sm",
    badgePadding: "px-2.5 py-1 text-xs",
  },
};

const getColumnKey = (column, index) =>
  column.id || `${column.field}_${column.label || index}`;

const syncAuthHeader = (headers) => {
  if (headers?.authorization) {
    const incomingToken = headers.authorization.split(" ")[1];
    if (incomingToken && getCookie("_AT") !== incomingToken) {
      setCookie("_AT", incomingToken);
    }
  }
};

export default function EnhancedDynamicList({
  columns,
  data,
  deleteClicked,
  title,
  editClick,
  createRoles,
  duplicateClicked,
  deleteRoles,
  editRoles,
  apiRoute,
  tableNameInDb,
  bulkActions = null,
  callPopUp,
  hasImport = false,
  importConfig = null,
}) {
  const [searchParams, setSearchParams] = useSearchParams();
  const location = useLocation();
  const navigate = useNavigate();
  const currentRole = getCookie("_r");

  const storageScope = tableNameInDb || apiRoute || "default";
  const prefsStorageKey = `glide_table_prefs_v2_${storageScope}`;
  const viewsStorageKey = `glide_table_views_v2_${storageScope}`;

  const currentPage = Number(searchParams.get("page")) || 1;
  const sDirection = searchParams.get("sortDirection");
  const sortBy = searchParams.get("sortBy");
  const q = searchParams.get("q");
  const activeFiltersQuery = searchParams.get("activeFilters");
  const initialFilterOperator = searchParams.get("filterOperator") || "_and";
  const initialSortRelSub = searchParams.get("sortingRelationSubValue") || null;
  const initialSortRelWhereField =
    searchParams.get("sortingRelationWhereField") || null;
  const initialSortRelWhereValue =
    searchParams.get("sortingRelationWhereValue") || null;

  const normalizedColumns = useMemo(
    () =>
      (columns || []).map((col, idx) => ({
        ...col,
        _colKey: getColumnKey(col, idx),
        _defaultVisible: col.hide !== true,
      })),
    [columns]
  );

  const loadSavedPrefs = useCallback(() => {
    try {
      const raw = localStorage.getItem(prefsStorageKey);
      return raw ? JSON.parse(raw) : null;
    } catch (e) {
      return null;
    }
  }, [prefsStorageKey]);

  const savedPrefs = useMemo(() => loadSavedPrefs(), [loadSavedPrefs]);

  // Track user-explicitly-toggled columns so code-level default changes are respected
  const [userToggledColumns, setUserToggledColumns] = useState(
    () => savedPrefs?.userToggledColumns || {}
  );

  const columnVisibility = useMemo(() => {
    const vis = {};
    normalizedColumns.forEach((c) => {
      if (Object.prototype.hasOwnProperty.call(userToggledColumns, c._colKey)) {
        vis[c._colKey] = userToggledColumns[c._colKey];
      } else {
        vis[c._colKey] = c._defaultVisible;
      }
    });
    return vis;
  }, [normalizedColumns, userToggledColumns]);

  const [columnOrder, setColumnOrder] = useState(() => {
    const defaultOrder = normalizedColumns.map((c) => c._colKey);
    if (Array.isArray(savedPrefs?.columnOrder)) {
      const validSaved = savedPrefs.columnOrder.filter((k) =>
        defaultOrder.includes(k)
      );
      const missing = defaultOrder.filter((k) => !validSaved.includes(k));
      return [...validSaved, ...missing];
    }
    return defaultOrder;
  });

  const [columnWidths, setColumnWidths] = useState(
    () => savedPrefs?.columnWidths || {}
  );
  const [density, setDensity] = useState(
    () => savedPrefs?.density || "normal"
  );

  const [savedViews, setSavedViews] = useState(() => {
    try {
      const raw = localStorage.getItem(viewsStorageKey);
      return raw ? JSON.parse(raw) : [];
    } catch (e) {
      return [];
    }
  });
  const [activeViewId, setActiveViewId] = useState(null);
  const [newViewName, setNewViewName] = useState("");

  useEffect(() => {
    try {
      localStorage.setItem(
        prefsStorageKey,
        JSON.stringify({
          userToggledColumns,
          columnOrder,
          columnWidths,
          density,
        })
      );
    } catch (e) {
      // ignore storage errors
    }
  }, [prefsStorageKey, userToggledColumns, columnOrder, columnWidths, density]);

  const updateSavedViewsStorage = (views) => {
    setSavedViews(views);
    try {
      localStorage.setItem(viewsStorageKey, JSON.stringify(views));
    } catch (e) {
      // ignore storage errors
    }
  };

  const [showColumnMenu, setShowColumnMenu] = useState(false);
  const [showViewsMenu, setShowViewsMenu] = useState(false);
  const [showFilterBuilder, setShowFilterBuilder] = useState(false);
  const columnMenuRef = useRef(null);
  const viewsMenuRef = useRef(null);

  const [draggedColKey, setDraggedColKey] = useState(null);
  const [dragOverColKey, setDragOverColKey] = useState(null);
  const resizingRef = useRef(null);

  const [isExportModalOpen, setIsExportModalOpen] = useState(false);
  const [isImportModalOpen, setIsImportModalOpen] = useState(false);
  const [sortDirection, setSortDirection] = useState(sDirection || "asc");
  const [sortedField, setSortedField] = useState(sortBy || null);
  const [sortRelSubValue, setSortRelSubValue] = useState(initialSortRelSub);
  const [sortRelWhereField, setSortRelWhereField] = useState(
    initialSortRelWhereField
  );
  const [sortRelWhereValue, setSortRelWhereValue] = useState(
    initialSortRelWhereValue
  );

  const [totalCount, setTotalCount] = useState(
    Array.isArray(data) ? data.length : data?.totalCount || 0
  );
  const [allData, setAllData] = useState(
    Array.isArray(data) ? data : data?.data || []
  );
  const [isLoading, setIsLoading] = useState(false);
  const [searchTerm, setSearchTerm] = useState(q || "");
  const [page, setPage] = useState(currentPage);
  const [uiPage, setUiPage] = useState(() => (currentPage - 1) * 5 + 1);
  const [divHeight, setDivHeight] = useState(0);
  const [filterBy, setFilterBy] = useState({ name: "", value: "" });
  const [filterValue, setFilterValue] = useState("");
  const [statusOptions, setStatusOptions] = useState([]);
  const [isLoadingOptions, setIsLoadingOptions] = useState(false);
  const [dateComparisonOperator, setDateComparisonOperator] = useState("_gte");
  const [activeFilters, setActiveFilters] = useState(() => {
    if (activeFiltersQuery != null) {
      try {
        return JSON.parse(activeFiltersQuery);
      } catch (e) {
        return [];
      }
    }
    return [];
  });
  const [filterOperator, setFilterOperator] = useState(initialFilterOperator);

  // Sync local table data whenever parent ListFormTemplate updates `data` prop
  useEffect(() => {
    if (Array.isArray(data)) {
      setAllData(data);
      setTotalCount(data.length);
    } else if (data && Array.isArray(data.data)) {
      setAllData(data.data);
      setTotalCount(data.totalCount ?? data.data.length);
    }
  }, [data]);

  const bulkActionsEnabled = bulkActions?.enabled === true;
  const [selectedItems, setSelectedItems] = useState([]);
  const [showBulkActionPopup, setShowBulkActionPopup] = useState(false);
  const [bulkActionProcessing, setBulkActionProcessing] = useState(false);

  useEffect(() => {
    const handleClickOutside = (e) => {
      if (columnMenuRef.current && !columnMenuRef.current.contains(e.target)) {
        setShowColumnMenu(false);
      }
      if (viewsMenuRef.current && !viewsMenuRef.current.contains(e.target)) {
        setShowViewsMenu(false);
      }
    };
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  useEffect(() => {
    const updateDivHeight = () => {
      const divElement = document.getElementById("enhancedListContainer");
      if (!divElement) return;
      const currentY = divElement.getBoundingClientRect().top;
      const screenHeight = window.innerHeight;
      const heightDifference = screenHeight - currentY - 105;
      setDivHeight(heightDifference > 280 ? heightDifference : 480);
    };

    updateDivHeight();
    window.addEventListener("resize", updateDivHeight);
    return () => window.removeEventListener("resize", updateDivHeight);
  }, [showFilterBuilder, activeFilters.length]);

  const orderedColumns = useMemo(() => {
    const colMap = new Map(normalizedColumns.map((c) => [c._colKey, c]));
    return columnOrder.map((key) => colMap.get(key)).filter(Boolean);
  }, [normalizedColumns, columnOrder]);

  const visibleColumns = useMemo(
    () =>
      orderedColumns.filter((col) => columnVisibility[col._colKey] !== false),
    [orderedColumns, columnVisibility]
  );

  const statusColumn = useMemo(
    () =>
      normalizedColumns.find(
        (c) => c.field === "status" && c.type === "status" && c.values
      ),
    [normalizedColumns]
  );

  const quickStatusValues = useMemo(
    () => (statusColumn?.values ? Object.keys(statusColumn.values) : []),
    [statusColumn]
  );

  const activeQuickStatus = useMemo(() => {
    if (!statusColumn) return null;
    const statusFilters = activeFilters.filter((f) => f.field === "status");
    if (
      statusFilters.length === 1 &&
      typeof statusFilters[0].value === "string"
    ) {
      return statusFilters[0].value;
    }
    return null;
  }, [activeFilters, statusColumn]);

  const isColumnSorted = useCallback(
    (column) => {
      if (column.field === "concat") {
        const concatKey = column.concat?.[0]?.subvalue
          ? `${column.concat[0].field}.${column.concat[0].subvalue}`
          : column.concat?.[0]?.field;
        return sortedField === concatKey;
      }
      if (sortedField !== column.field) return false;

      if (column.sortingRelationSubValue != null) {
        if (sortRelSubValue !== `${column.sortingRelationSubValue}`) {
          return false;
        }
        if (column.sortingRelationWhereField != null) {
          return (
            sortRelWhereField === `${column.sortingRelationWhereField}` &&
            sortRelWhereValue === `${column.sortingRelationWhereValue}`
          );
        }
        return !sortRelWhereField;
      }

      return !sortRelSubValue;
    },
    [sortedField, sortRelSubValue, sortRelWhereField, sortRelWhereValue]
  );

  const fetchData = async ({
    pageOverride,
    uiPageOverride,
    sortByOverride,
    sortDirectionOverride,
    searchOverride,
    filterByOverride,
    filterValueOverride,
    filtersOverride,
    filterOperatorOverride,
    isRelation,
    sortingRelationSubValue,
    sortingRelationWhereField,
    sortingRelationWhereValue,
  } = {}) => {
    try {
      setIsLoading(true);
      const _r = await waitForCookie("_r");

      const queryParams = new URLSearchParams();
      queryParams.set("currentRole", _r);
      queryParams.set("page", pageOverride ?? page);

      const effectiveSortBy =
        sortByOverride !== undefined ? sortByOverride : sortedField;
      const effectiveSortDirection =
        sortDirectionOverride ?? sortDirection;
      const effectiveOperator = filterOperatorOverride ?? filterOperator;

      const effectiveRelSub =
        sortingRelationSubValue !== undefined
          ? sortingRelationSubValue
          : sortRelSubValue;
      const effectiveRelWhereField =
        sortingRelationWhereField !== undefined
          ? sortingRelationWhereField
          : sortRelWhereField;
      const effectiveRelWhereValue =
        sortingRelationWhereValue !== undefined
          ? sortingRelationWhereValue
          : sortRelWhereValue;

      if (effectiveSortBy) {
        queryParams.set("sortBy", effectiveSortBy);
        queryParams.set("sortDirection", effectiveSortDirection);
      }

      const effectiveSearch =
        searchOverride !== undefined ? searchOverride : searchTerm;
      if (effectiveSearch && effectiveSearch.trim() !== "") {
        queryParams.set("q", effectiveSearch);
      }

      const filtersToUse = filtersOverride ?? activeFilters;
      if (filtersToUse.length > 0) {
        queryParams.set("activeFilters", JSON.stringify(filtersToUse));
        queryParams.set("filterOperator", effectiveOperator);
      }
      if (isRelation === true) {
        queryParams.set("isRelation", isRelation);
      }
      if (effectiveRelSub != null) {
        queryParams.set("sortingRelationSubValue", effectiveRelSub);
      }
      if (effectiveRelWhereField != null && effectiveRelWhereValue != null) {
        queryParams.set("sortingRelationWhereField", effectiveRelWhereField);
        queryParams.set("sortingRelationWhereValue", effectiveRelWhereValue);
      }

      const resp = await axios.get(
        `${config.apiUrl}/api/${apiRoute}?${queryParams.toString()}`,
        {
          headers: {
            Authorization: `Bearer ${getCookie("_AT")}`,
          },
        }
      );

      syncAuthHeader(resp.headers);

      setAllData(resp.data?.data || []);
      setTotalCount(resp.data?.totalCount || 0);
      setPage(pageOverride ?? page);
      if (uiPageOverride !== undefined) {
        setUiPage(uiPageOverride);
      } else if (pageOverride === 1) {
        setUiPage(1);
      }
      setSortedField(effectiveSortBy);
      setSortDirection(effectiveSortDirection);
      setSortRelSubValue(effectiveRelSub);
      setSortRelWhereField(effectiveRelWhereField);
      setSortRelWhereValue(effectiveRelWhereValue);
      setSearchParams(Object.fromEntries(queryParams.entries()));
    } catch (e) {
      console.error("Fetch error:", e);
    } finally {
      setIsLoading(false);
    }
  };

  const getCurrentOverrides = (overrides = {}) => ({
    pageOverride: page,
    searchOverride: searchTerm,
    sortByOverride: sortedField,
    sortDirectionOverride: sortDirection,
    filterByOverride: filterBy.value,
    filterValueOverride: filterValue,
    sortingRelationSubValue: sortRelSubValue,
    sortingRelationWhereField: sortRelWhereField,
    sortingRelationWhereValue: sortRelWhereValue,
    ...overrides,
  });

  const toggleSort = async (column) => {
    let field = column.field;
    if (field === "concat") {
      field = column.concat[0].subvalue
        ? `${column.concat[0].field}.${column.concat[0].subvalue}`
        : column.concat[0].field;
    }

    const currentlySortedOnThisCol = isColumnSorted(column);
    const newDirection =
      currentlySortedOnThisCol && sortDirection === "asc" ? "desc" : "asc";

    const currentOverrides = {
      pageOverride: 1,
      sortByOverride: field,
      sortDirectionOverride: newDirection,
      sortingRelationSubValue: column.sortingRelationSubValue ?? null,
      sortingRelationWhereField:
        column.sortingRelationWhereField != null &&
        column.sortingRelationWhereValue != null
          ? column.sortingRelationWhereField
          : null,
      sortingRelationWhereValue:
        column.sortingRelationWhereField != null &&
        column.sortingRelationWhereValue != null
          ? column.sortingRelationWhereValue
          : null,
    };

    await fetchData(getCurrentOverrides(currentOverrides));
  };

  const handleQuickStatusSelect = async (statusVal) => {
    const nonStatusFilters = activeFilters.filter((f) => f.field !== "status");
    const updatedFilters =
      statusVal === null || activeQuickStatus === statusVal
        ? nonStatusFilters
        : [
            ...nonStatusFilters,
            { field: "status", value: statusVal, operator: "_and" },
          ];
    setActiveFilters(updatedFilters);
    await fetchData({
      pageOverride: 1,
      filtersOverride: updatedFilters,
    });
  };

  const handleResizeStart = (e, colKey) => {
    e.stopPropagation();
    e.preventDefault();
    const thElement = e.currentTarget.parentElement;
    const startWidth = thElement ? thElement.offsetWidth : 140;
    const startX = e.clientX;

    resizingRef.current = { colKey, startX, startWidth };

    const handleMouseMove = (moveEvent) => {
      if (!resizingRef.current) return;
      const delta = moveEvent.clientX - resizingRef.current.startX;
      const nextWidth = Math.max(70, resizingRef.current.startWidth + delta);
      setColumnWidths((prev) => ({
        ...prev,
        [resizingRef.current.colKey]: nextWidth,
      }));
    };

    const handleMouseUp = () => {
      resizingRef.current = null;
      document.removeEventListener("mousemove", handleMouseMove);
      document.removeEventListener("mouseup", handleMouseUp);
    };

    document.addEventListener("mousemove", handleMouseMove);
    document.addEventListener("mouseup", handleMouseUp);
  };

  const resetColumnWidth = (e, colKey) => {
    e.stopPropagation();
    setColumnWidths((prev) => {
      const next = { ...prev };
      delete next[colKey];
      return next;
    });
  };

  const handleColumnDragStart = (e, colKey) => {
    setDraggedColKey(colKey);
    e.dataTransfer.effectAllowed = "move";
  };

  const handleColumnDragOver = (e, colKey) => {
    e.preventDefault();
    if (draggedColKey && draggedColKey !== colKey) {
      setDragOverColKey(colKey);
    }
  };

  const handleColumnDrop = (e, targetColKey) => {
    e.preventDefault();
    if (!draggedColKey || draggedColKey === targetColKey) {
      setDraggedColKey(null);
      setDragOverColKey(null);
      return;
    }
    setColumnOrder((prev) => {
      const next = [...prev];
      const fromIdx = next.indexOf(draggedColKey);
      const toIdx = next.indexOf(targetColKey);
      if (fromIdx === -1 || toIdx === -1) return prev;
      next.splice(fromIdx, 1);
      next.splice(toIdx, 0, draggedColKey);
      return next;
    });
    setDraggedColKey(null);
    setDragOverColKey(null);
  };

  const moveColumnInOrder = (colKey, direction) => {
    setColumnOrder((prev) => {
      const idx = prev.indexOf(colKey);
      if (idx === -1) return prev;
      const targetIdx = direction === "up" ? idx - 1 : idx + 1;
      if (targetIdx < 0 || targetIdx >= prev.length) return prev;
      const next = [...prev];
      const [removed] = next.splice(idx, 1);
      next.splice(targetIdx, 0, removed);
      return next;
    });
  };

  const resetTableLayoutToDefaults = () => {
    setUserToggledColumns({});
    setColumnOrder(normalizedColumns.map((c) => c._colKey));
    setColumnWidths({});
    setDensity("normal");
    setActiveViewId(null);
  };

  const handleSaveCurrentView = (e) => {
    e.preventDefault();
    const trimmed = newViewName.trim();
    if (!trimmed) return;
    const newView = {
      id: `view_${Date.now()}`,
      name: trimmed,
      userToggledColumns,
      columnOrder,
      columnWidths,
      density,
      activeFilters,
      filterOperator,
      searchTerm,
      sortedField,
      sortDirection,
      sortRelSubValue,
      sortRelWhereField,
      sortRelWhereValue,
    };
    const updated = [...savedViews, newView];
    updateSavedViewsStorage(updated);
    setActiveViewId(newView.id);
    setNewViewName("");
  };

  const handleApplySavedView = async (view) => {
    setActiveViewId(view.id);
    if (view.userToggledColumns) {
      setUserToggledColumns(view.userToggledColumns);
    }
    if (view.columnOrder) setColumnOrder(view.columnOrder);
    if (view.columnWidths) setColumnWidths(view.columnWidths);
    if (view.density) setDensity(view.density);

    const nextFilters = view.activeFilters || [];
    const nextOperator = view.filterOperator || "_and";
    const nextSearch = view.searchTerm ?? "";
    const nextSortField = view.sortedField ?? null;
    const nextSortDir = view.sortDirection || "asc";
    const nextRelSub = view.sortRelSubValue ?? null;
    const nextRelWhereField = view.sortRelWhereField ?? null;
    const nextRelWhereValue = view.sortRelWhereValue ?? null;

    setActiveFilters(nextFilters);
    setFilterOperator(nextOperator);
    setSearchTerm(nextSearch);
    setSortedField(nextSortField);
    setSortDirection(nextSortDir);
    setSortRelSubValue(nextRelSub);
    setSortRelWhereField(nextRelWhereField);
    setSortRelWhereValue(nextRelWhereValue);
    setShowViewsMenu(false);

    await fetchData({
      pageOverride: 1,
      filtersOverride: nextFilters,
      filterOperatorOverride: nextOperator,
      searchOverride: nextSearch,
      sortByOverride: nextSortField,
      sortDirectionOverride: nextSortDir,
      sortingRelationSubValue: nextRelSub,
      sortingRelationWhereField: nextRelWhereField,
      sortingRelationWhereValue: nextRelWhereValue,
    });
  };

  const handleDeleteSavedView = (e, viewId) => {
    e.stopPropagation();
    const updated = savedViews.filter((v) => v.id !== viewId);
    updateSavedViewsStorage(updated);
    if (activeViewId === viewId) {
      setActiveViewId(null);
    }
  };

  const UI_PAGE_SIZE = 10;
  const UI_PAGES_PER_BATCH = 5;

  const displayedData = useMemo(() => {
    if (!Array.isArray(allData) || allData.length === 0) return [];
    if (allData.length > UI_PAGE_SIZE * UI_PAGES_PER_BATCH) {
      const start = (uiPage - 1) * UI_PAGE_SIZE;
      return allData.slice(start, start + UI_PAGE_SIZE);
    }
    const sliceIndex = (uiPage - 1) % UI_PAGES_PER_BATCH;
    const start = sliceIndex * UI_PAGE_SIZE;
    return allData.slice(start, start + UI_PAGE_SIZE);
  }, [allData, uiPage]);

  const formattedCellCache = useMemo(() => {
    const cache = new Map();
    if (!Array.isArray(displayedData)) return cache;

    const applyMaxChars = (str, maxChars) => {
      if (str == null) return { display: "", full: "", truncated: false };
      const fullStr = String(str);
      if (maxChars && fullStr.length > maxChars) {
        return {
          display: `${fullStr.slice(0, maxChars)}..`,
          full: fullStr,
          truncated: true,
        };
      }
      return { display: fullStr, full: fullStr, truncated: false };
    };

    displayedData.forEach((row) => {
      const rowCells = {};
      const branchCode = row.branch?.code;

      visibleColumns.forEach((column) => {
        const colKey = column._colKey;

        if (column.type === "status") {
          const raw = formatInput(row, column);
          const textClass = column.values
            ? column.values[raw] || "text-gray-700 ring-gray-600/20 bg-gray-50"
            : "text-green-700 ring-green-600/20 bg-green-50";
          let displayLabel = raw;
          if (column.displayValues && raw != null) {
            const mapped = column.displayValues[String(raw)];
            if (mapped != null) displayLabel = mapped;
          }
          rowCells[colKey] = {
            kind: "status",
            raw,
            display: displayLabel,
            textClass,
          };
          return;
        }

        if (column.type === "color") {
          const colorVal = formatInput(row, column);
          const labelVal = !column.showValue
            ? colorVal
            : formatInput(row, column, true, false);
          rowCells[colKey] = {
            kind: "color",
            colorVal,
            display: labelVal,
          };
          return;
        }

        if (
          column.type === "condition" &&
          column.trueCondition &&
          column.elseCondition
        ) {
          const isTrue = formatInput(row, column) === column.trueValue;
          const targetCond = isTrue
            ? column.trueCondition
            : column.elseCondition;
          let condText = "";
          if (targetCond.field === "concat") {
            condText = Array.isArray(targetCond.concat)
              ? targetCond.concat
                  .map(
                    (col, i, arr) =>
                      formatInput(row, col) +
                      (i + 1 !== arr.length ? targetCond.concatBy ?? " " : "")
                  )
                  .join("")
              : "";
          } else {
            condText = formatInput(row, targetCond);
          }
          const { display, full, truncated } = applyMaxChars(
            condText,
            column.maxChars
          );
          rowCells[colKey] = { kind: "text", display, full, truncated };
          return;
        }

        if (column.field === "concat" && Array.isArray(column.concat)) {
          const joined = column.concat
            .map(
              (col, i, arr) =>
                formatInput(row, col) +
                (i + 1 !== arr.length ? column.concatBy ?? " " : "")
            )
            .join("");
          const { display, full, truncated } = applyMaxChars(
            joined,
            column.maxChars
          );
          rowCells[colKey] = {
            kind: column.imageField ? "image_concat" : "text",
            display,
            full,
            truncated,
            imageSrc: column.imageField
              ? `${config.apiUrl}/assets/${row[column.imageField]?.name}`
              : null,
          };
          return;
        }

        let rawText = formatInput(
          row,
          column,
          undefined,
          undefined,
          branchCode != null ? branchCode : undefined
        );
        if (Array.isArray(rawText)) {
          const relPath =
            column?.subvalue || column?.relationSubvalue || column?.relationWith;
          rawText = rawText
            .map((item) => {
              if (item == null) return "";
              if (typeof item !== "object") return String(item);
              if (relPath) {
                let val = item;
                for (const part of relPath.split(".")) {
                  if (val != null && typeof val === "object" && part in val) {
                    val = val[part];
                  } else {
                    val = "";
                    break;
                  }
                }
                if (val != null && typeof val !== "object") return String(val);
              }
              return (
                item.Name ??
                item.name ??
                item.branch?.Name ??
                item.branch?.name ??
                item.code ??
                ""
              );
            })
            .filter((v) => v !== null && v !== undefined && v !== "")
            .join(", ");
        } else if (rawText !== null && typeof rawText === "object") {
          const relPath =
            column?.subvalue || column?.relationSubvalue || column?.relationWith;
          if (relPath) {
            let val = rawText;
            for (const part of relPath.split(".")) {
              if (val != null && typeof val === "object" && part in val) {
                val = val[part];
              } else {
                val = "";
                break;
              }
            }
            rawText = val;
          }
          if (rawText !== null && typeof rawText === "object") {
            rawText =
              rawText.Name ??
              rawText.name ??
              rawText.branch?.Name ??
              rawText.branch?.name ??
              rawText.code ??
              rawText.title ??
              "";
          }
        }
        const { display, full, truncated } = applyMaxChars(
          rawText,
          column.maxChars
        );

        if (column.imageField) {
          rowCells[colKey] = {
            kind: "image_single",
            display,
            full,
            truncated,
            imageSrc: `${config.apiUrl}/assets/${row[column.imageField.name]}`,
          };
          return;
        }

        rowCells[colKey] = {
          kind: "text",
          display,
          full,
          truncated,
        };
      });

      cache.set(row.id, rowCells);
    });

    return cache;
  }, [displayedData, visibleColumns]);

  const getItemName = (row) => {
    const nameField = bulkActions?.nameField || "Name";
    return row[nameField] || row.Name || row.name || `Item #${row.id}`;
  };

  const toggleItemSelection = (itemId, itemName) => {
    setSelectedItems((prev) => {
      const exists = prev.find((item) => item.id === itemId);
      if (exists) {
        return prev.filter((item) => item.id !== itemId);
      }
      return [...prev, { id: itemId, name: itemName }];
    });
  };

  const toggleSelectAll = () => {
    const allPageSelected =
      displayedData.length > 0 &&
      displayedData.every((row) =>
        selectedItems.some((item) => item.id === row.id)
      );
    if (allPageSelected) {
      const pageIds = new Set(displayedData.map((r) => r.id));
      setSelectedItems((prev) => prev.filter((item) => !pageIds.has(item.id)));
    } else {
      setSelectedItems((prev) => {
        const existingIds = new Set(prev.map((item) => item.id));
        const additions = displayedData
          .filter((row) => !existingIds.has(row.id))
          .map((row) => ({
            id: row.id,
            name: getItemName(row),
          }));
        return [...prev, ...additions];
      });
    }
  };

  const isItemSelected = (itemId) =>
    selectedItems.some((item) => item.id === itemId);

  const handleBulkAction = async (
    businessDateTime,
    selectedBranchCodes,
    companies
  ) => {
    if (!bulkActions?.onAction) return;

    setBulkActionProcessing(true);
    try {
      const result = await bulkActions.onAction({
        selectedItems,
        businessDateTime,
        companies,
      });

      setSelectedItems([]);
      setShowBulkActionPopup(false);

      if (callPopUp && typeof callPopUp === "function") {
        callPopUp({
          isError: false,
          title: "Success",
          description: result?.message || "Bulk action completed successfully",
        });
      }

      await fetchData();
      return result;
    } catch (error) {
      console.error("Error performing bulk action:", error);
      setSelectedItems([]);
      setShowBulkActionPopup(false);

      if (callPopUp && typeof callPopUp === "function") {
        callPopUp({
          isError: true,
          title: "Error",
          description: error?.message || "Failed to complete bulk action",
        });
      }
    } finally {
      setBulkActionProcessing(false);
    }
  };

  const handleCSVExport = async (exportData) => {
    try {
      const apiData = {
        fields: exportData.fields.map((field) => ({
          field: field.field,
          label: field.label,
          concat: field.concat,
          subvalue: field.subvalue || field.relationSubvalue,
          isRelation: field.isRelation,
          relationWith: field.relationWith,
          where: field.where,
          isArray: field.isArray,
          showValue: field.showValue,
          isDate: field.isDate,
          branchCode: field.branchCode,
        })),
        filters: activeFilters,
        searchTerm: searchTerm,
        sortBy: sortedField,
        sortDirection: sortDirection,
        timestamp: exportData.timestamp,
        currentTable: tableNameInDb,
      };

      const response = await axios.post(
        `${config.apiUrl}/api/exports/export-csv`,
        apiData,
        {
          headers: {
            Authorization: `Bearer ${getCookie("_AT")}`,
            "Content-Type": "application/json",
          },
          responseType: "blob",
        }
      );

      syncAuthHeader(response.headers);

      const blob = new Blob([response.data], { type: "text/csv" });
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = `${title || tableNameInDb || "data"}-export-${
        new Date().toISOString().split("T")[0]
      }.csv`;
      document.body.appendChild(a);
      a.click();
      window.URL.revokeObjectURL(url);
      document.body.removeChild(a);
    } catch (error) {
      console.error("Export failed:", error);
      alert("Export failed. Please try again.");
    }
  };

  const clearAllFiltersAndSearch = async () => {
    const clearedFilters = [];
    setSearchTerm("");
    setFilterBy({ name: "", value: "" });
    setFilterValue("");
    setSortedField(null);
    setSortDirection("asc");
    setSortRelSubValue(null);
    setSortRelWhereField(null);
    setSortRelWhereValue(null);
    setPage(1);
    setUiPage(1);
    setActiveFilters(clearedFilters);
    setActiveViewId(null);
    await fetchData({
      pageOverride: 1,
      uiPageOverride: 1,
      searchOverride: "",
      sortByOverride: null,
      sortDirectionOverride: "asc",
      filtersOverride: clearedFilters,
      sortingRelationSubValue: null,
      sortingRelationWhereField: null,
      sortingRelationWhereValue: null,
    });
    setSearchParams({});
  };

  const handleUiPageChange = async (nextUiPage) => {
    const maxUiPages = Math.max(1, Math.ceil(totalCount / UI_PAGE_SIZE));
    if (nextUiPage < 1 || nextUiPage > maxUiPages) return;
    const targetBackendPage = Math.ceil(nextUiPage / UI_PAGES_PER_BATCH);
    setUiPage(nextUiPage);
    if (
      targetBackendPage !== page &&
      allData.length <= UI_PAGE_SIZE * UI_PAGES_PER_BATCH &&
      allData.length < totalCount
    ) {
      await fetchData(
        getCurrentOverrides({
          pageOverride: targetBackendPage,
          uiPageOverride: nextUiPage,
        })
      );
    }
  };

  const densityConfig = DENSITY_OPTIONS[density] || DENSITY_OPTIONS.normal;

  const getEntityName = (pathname) => {
    if (pathname === "/vehicles") return "vehicles";
    if (pathname === "/customers") return "drivers";
    if (pathname === "/reservations") return "reservations";
    if (pathname === "/companies") return "companies";
    if (pathname === "/users") return "users";
    if (pathname === "/cities") return "cities";
    if (pathname === "/branches") return "branches";
    if (pathname === "/fleetlocations") return "fleet locations";
    if (pathname === "/vehicletypes") return "vehicle types";
    if (pathname === "/vehiclebrands") return "vehicle brands";
    if (pathname === "/vehiclemodels") return "vehicle models";
    if (pathname === "/vehicleclasses") return "vehicle categories";
    if (pathname === "/colors") return "colors";
    if (pathname === "/damagetypes") return "damage types";
    if (pathname === "/addoncategories") return "extras categories";
    if (pathname === "/addons") return "extras";
    if (pathname === "/mileageoptions") return "mileage options";
    if (pathname === "/coverageoptions") return "coverage options";
    if (pathname === "/seasons") return "seasons";
    if (pathname === "/vehiclerates") return "vehicle rates";
    if (pathname === "/extrasrates") return "extras rates";
    if (pathname === "/mileageoptionsrates") return "mileage option rates";
    if (pathname === "/coverageoptionsrates") return "coverage option rates";
    if (pathname === "/deliveryrates") return "delivery rates";
    if (pathname === "/currencies") return "currencies";
    if (pathname === "/currencyexchanges") return "currency exchanges";
    if (pathname === "/servicetypes") return "service types";
    return "records";
  };

  const entityName = getEntityName(window.location.pathname);
  const firstRecord =
    totalCount === 0 || !displayedData.length
      ? 0
      : (uiPage - 1) * UI_PAGE_SIZE + 1;
  const lastRecord =
    totalCount === 0 || !displayedData.length
      ? 0
      : Math.min(totalCount, (uiPage - 1) * UI_PAGE_SIZE + displayedData.length);

  const exportableColumns = useMemo(
    () =>
      orderedColumns.map((col) => ({
        ...col,
        hide: columnVisibility[col._colKey] === false && !col.sendToExport,
      })),
    [orderedColumns, columnVisibility]
  );

  return (
    <div className="pb-4">
      <div className="relative z-10 min-w-0 max-w-full bg-white border border-gray-200 rounded-xl shadow-sm">
        {/* Card Header / Control Bar - relative z-20 keeps filter dropdowns above sticky table header while staying below PageTitle (z-30) */}
        <div className="relative z-20 p-4 sm:p-5 border-b border-gray-200 bg-white rounded-t-xl">
          <div className="flex flex-col lg:flex-row lg:items-center lg:justify-between gap-3">
            {/* Left: Search Input + Filter Drawer Toggle + Quick Status Pills */}
            <div className="flex flex-wrap items-center gap-2.5 flex-1">
              <form
                className="relative w-full sm:w-72"
                onSubmit={async (e) => {
                  e.preventDefault();
                  await fetchData({
                    pageOverride: 1,
                    searchOverride: searchTerm,
                  });
                }}
              >
                <MagnifyingGlassIcon className="pointer-events-none absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-gray-400" />
                <input
                  placeholder={`Search ${entityName}...`}
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  className="block w-full rounded-lg border border-gray-300 bg-white py-1.5 pl-9 pr-8 text-gray-900 placeholder:text-gray-400 focus:border-blue-600 focus:outline-none focus:ring-2 focus:ring-blue-500/20 sm:text-sm transition"
                />
                {searchTerm && (
                  <button
                    type="button"
                    onClick={() => {
                      setSearchTerm("");
                      fetchData({ pageOverride: 1, searchOverride: "" });
                    }}
                    className="absolute right-2.5 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600"
                    title="Clear search"
                  >
                    <XMarkIcon className="h-4 w-4" />
                  </button>
                )}
              </form>

              {normalizedColumns.some((c) => c.showInFilter) && (
                <button
                  type="button"
                  onClick={() => setShowFilterBuilder((prev) => !prev)}
                  className={classNames(
                    "inline-flex items-center gap-1.5 rounded-lg px-3 py-1.5 text-xs font-medium border transition shadow-sm",
                    showFilterBuilder || activeFilters.length > 0
                      ? "bg-blue-50 text-blue-700 border-blue-200"
                      : "bg-white text-gray-700 border-gray-300 hover:bg-gray-50"
                  )}
                >
                  <FunnelIcon className="h-3.5 w-3.5" />
                  <span>Filters</span>
                  {activeFilters.length > 0 && (
                    <span className="ml-0.5 rounded-full bg-blue-600 px-1.5 py-0.2 text-[10px] font-semibold text-white">
                      {activeFilters.length}
                    </span>
                  )}
                </button>
              )}

              {quickStatusValues.length > 0 && (
                <div className="hidden xl:flex items-center gap-1 bg-gray-100 p-1 rounded-lg border border-gray-200/60">
                  <button
                    type="button"
                    onClick={() => handleQuickStatusSelect(null)}
                    className={classNames(
                      "px-2.5 py-1 rounded-md text-xs font-medium transition",
                      activeQuickStatus === null
                        ? "bg-white text-gray-900 shadow-sm font-semibold"
                        : "text-gray-600 hover:text-gray-900"
                    )}
                  >
                    All
                  </button>
                  {quickStatusValues.map((statusVal) => (
                    <button
                      key={statusVal}
                      type="button"
                      onClick={() => handleQuickStatusSelect(statusVal)}
                      className={classNames(
                        "px-2.5 py-1 rounded-md text-xs font-medium transition whitespace-nowrap",
                        activeQuickStatus === statusVal
                          ? "bg-white text-blue-600 shadow-sm font-semibold"
                          : "text-gray-600 hover:text-gray-900"
                      )}
                    >
                      {statusVal}
                    </button>
                  ))}
                </div>
              )}

              {isLoading && (
                <div className="flex items-center gap-1.5 text-xs text-blue-600 font-medium ml-1">
                  <ArrowPathIcon className="h-4 w-4 animate-spin text-blue-600" />
                  <span className="hidden sm:inline">Refreshing...</span>
                </div>
              )}
            </div>

            {/* Right: Density, Columns, Saved Views, Export/Import, Bulk Action */}
            <div className="flex flex-wrap items-center gap-2">
              {bulkActionsEnabled && selectedItems.length > 0 && (
                <button
                  type="button"
                  className="inline-flex items-center px-3 py-1.5 border border-blue-600 text-blue-700 bg-blue-50 hover:bg-blue-100 text-xs font-semibold rounded-lg transition shadow-sm"
                  onClick={() => setShowBulkActionPopup(true)}
                >
                  {bulkActions.buttonLabel || "Action"} ({selectedItems.length})
                </button>
              )}

              <div
                className="inline-flex rounded-lg bg-gray-100 p-0.5 border border-gray-200/80"
                title="Row density"
              >
                {Object.entries(DENSITY_OPTIONS).map(([key, opt]) => (
                  <button
                    key={key}
                    type="button"
                    onClick={() => setDensity(key)}
                    className={classNames(
                      "px-2.5 py-1 text-xs font-medium rounded-md transition",
                      density === key
                        ? "bg-white text-gray-900 shadow-sm font-semibold"
                        : "text-gray-500 hover:text-gray-800"
                    )}
                  >
                    {opt.label}
                  </button>
                ))}
              </div>

              {/* Column Customizer Popover */}
              <div className="relative" ref={columnMenuRef}>
                <button
                  type="button"
                  onClick={() => setShowColumnMenu((prev) => !prev)}
                  className={classNames(
                    "inline-flex items-center gap-1.5 rounded-lg px-3 py-1.5 text-xs font-medium border transition shadow-sm",
                    showColumnMenu
                      ? "bg-blue-50 text-blue-700 border-blue-200"
                      : "bg-white text-gray-700 border-gray-300 hover:bg-gray-50"
                  )}
                >
                  <ViewColumnsIcon className="h-4 w-4 text-gray-500" />
                  <span>Columns</span>
                  <span className="px-1.5 py-0.2 text-[11px] font-semibold bg-gray-100 text-gray-700 rounded-full">
                    {visibleColumns.length}/{normalizedColumns.length}
                  </span>
                </button>

                {showColumnMenu && (
                  <div className="fixed inset-x-4 top-1/2 -translate-y-1/2 z-50 mx-auto max-w-sm sm:absolute sm:inset-auto sm:right-0 sm:top-auto sm:translate-y-0 sm:z-30 sm:mt-2 sm:w-80 sm:max-w-none origin-top-right rounded-xl bg-white p-4 shadow-xl border border-gray-200">
                    <div className="flex items-center justify-between border-b border-gray-100 pb-2.5 mb-2.5">
                      <div>
                        <h4 className="text-xs font-bold uppercase tracking-wider text-gray-900">
                          Customize Columns
                        </h4>
                        <p className="text-[11px] text-gray-500">
                          Toggle visibility or reorder columns
                        </p>
                      </div>
                      <button
                        type="button"
                        onClick={resetTableLayoutToDefaults}
                        className="text-xs font-semibold text-blue-600 hover:underline"
                      >
                        Reset
                      </button>
                    </div>

                    <div className="max-h-72 overflow-y-auto space-y-1 pr-1">
                      {orderedColumns.map((col, idx) => {
                        const isVisible =
                          columnVisibility[col._colKey] !== false;
                        return (
                          <div
                            key={col._colKey}
                            className="flex items-center justify-between rounded-lg px-2 py-1.5 hover:bg-gray-50 text-xs"
                          >
                            <label className="flex items-center gap-2.5 cursor-pointer flex-1 truncate">
                              <input
                                type="checkbox"
                                checked={isVisible}
                                onChange={() =>
                                  setUserToggledColumns((prev) => ({
                                    ...prev,
                                    [col._colKey]: !isVisible,
                                  }))
                                }
                                className="h-3.5 w-3.5 rounded border-gray-300 text-blue-600 focus:ring-blue-500"
                              />
                              <span
                                className={classNames(
                                  "truncate",
                                  isVisible
                                    ? "font-medium text-gray-900"
                                    : "text-gray-400"
                                )}
                              >
                                {col.label}
                              </span>
                              {!col._defaultVisible && (
                                <span className="rounded bg-gray-100 px-1.5 py-0.5 text-[10px] text-gray-500">
                                  optional
                                </span>
                              )}
                            </label>

                            <div className="flex items-center gap-0.5 ml-2">
                              <button
                                type="button"
                                disabled={idx === 0}
                                onClick={() =>
                                  moveColumnInOrder(col._colKey, "up")
                                }
                                className="p-1 text-gray-400 hover:text-gray-700 disabled:opacity-30"
                                title="Move left"
                              >
                                <ChevronUpIcon className="h-3.5 w-3.5" />
                              </button>
                              <button
                                type="button"
                                disabled={idx === orderedColumns.length - 1}
                                onClick={() =>
                                  moveColumnInOrder(col._colKey, "down")
                                }
                                className="p-1 text-gray-400 hover:text-gray-700 disabled:opacity-30"
                                title="Move right"
                              >
                                <ChevronDownIcon className="h-3.5 w-3.5" />
                              </button>
                            </div>
                          </div>
                        );
                      })}
                    </div>

                    <div className="mt-3 pt-2.5 border-t border-gray-100 flex justify-between text-xs">
                      <button
                        type="button"
                        onClick={() => {
                          const allOn = {};
                          normalizedColumns.forEach((c) => {
                            allOn[c._colKey] = true;
                          });
                          setUserToggledColumns(allOn);
                        }}
                        className="text-gray-600 hover:text-blue-600 font-medium"
                      >
                        Show All
                      </button>
                      <button
                        type="button"
                        onClick={() => setUserToggledColumns({})}
                        className="text-gray-600 hover:text-blue-600 font-medium"
                      >
                        Default Columns
                      </button>
                    </div>
                  </div>
                )}
              </div>

              {/* Saved Views Popover */}
              <div className="relative" ref={viewsMenuRef}>
                <button
                  type="button"
                  onClick={() => setShowViewsMenu((prev) => !prev)}
                  className={classNames(
                    "inline-flex items-center gap-1.5 rounded-lg px-3 py-1.5 text-xs font-medium border transition shadow-sm",
                    activeViewId || showViewsMenu
                      ? "bg-blue-50 text-blue-700 border-blue-200"
                      : "bg-white text-gray-700 border-gray-300 hover:bg-gray-50"
                  )}
                >
                  <BookmarkSquareIcon className="h-4 w-4 text-gray-500" />
                  <span>
                    {savedViews.find((v) => v.id === activeViewId)?.name ||
                      "Views"}
                  </span>
                  {savedViews.length > 0 && (
                    <span className="px-1.5 py-0.2 text-[11px] font-semibold bg-gray-100 text-gray-700 rounded-full">
                      {savedViews.length}
                    </span>
                  )}
                </button>

                {showViewsMenu && (
                  <div className="fixed inset-x-4 top-1/2 -translate-y-1/2 z-50 mx-auto max-w-sm sm:absolute sm:inset-auto sm:right-0 sm:top-auto sm:translate-y-0 sm:z-30 sm:mt-2 sm:w-72 sm:max-w-none origin-top-right rounded-xl bg-white p-4 shadow-xl border border-gray-200">
                    <h4 className="text-xs font-bold uppercase tracking-wider text-gray-900 mb-1">
                      Saved Table Views
                    </h4>
                    <p className="text-[11px] text-gray-500 mb-3">
                      Save current columns, density, sort, and filters
                    </p>

                    <form
                      onSubmit={handleSaveCurrentView}
                      className="flex gap-1.5 mb-3"
                    >
                      <input
                        type="text"
                        placeholder="New view name..."
                        value={newViewName}
                        onChange={(e) => setNewViewName(e.target.value)}
                        className="block w-full rounded-lg border border-gray-300 py-1 px-2.5 text-xs text-gray-900 focus:border-blue-600 focus:outline-none focus:ring-2 focus:ring-blue-500/20"
                      />
                      <button
                        type="submit"
                        disabled={!newViewName.trim()}
                        className="inline-flex items-center gap-1 rounded-lg bg-blue-600 px-2.5 py-1 text-xs font-medium text-white hover:bg-blue-700 disabled:opacity-40 transition"
                      >
                        <PlusIcon className="h-3.5 w-3.5" />
                        Save
                      </button>
                    </form>

                    <div className="max-h-48 overflow-y-auto space-y-1">
                      {savedViews.length === 0 ? (
                        <p className="text-xs text-gray-400 py-2 text-center">
                          No saved views yet
                        </p>
                      ) : (
                        savedViews.map((view) => (
                          <div
                            key={view.id}
                            onClick={() => handleApplySavedView(view)}
                            className={classNames(
                              "flex items-center justify-between rounded-lg px-2.5 py-1.5 text-xs cursor-pointer transition",
                              activeViewId === view.id
                                ? "bg-blue-50 text-blue-700 font-semibold"
                                : "hover:bg-gray-50 text-gray-700"
                            )}
                          >
                            <div className="flex items-center gap-1.5 truncate">
                              {activeViewId === view.id && (
                                <CheckIcon className="h-3.5 w-3.5 text-blue-600 flex-shrink-0" />
                              )}
                              <span className="truncate">{view.name}</span>
                            </div>
                            <button
                              type="button"
                              onClick={(e) => handleDeleteSavedView(e, view.id)}
                              className="p-1 text-gray-400 hover:text-red-600"
                              title="Delete saved view"
                            >
                              <TrashIcon className="h-3.5 w-3.5" />
                            </button>
                          </div>
                        ))
                      )}
                    </div>
                  </div>
                )}
              </div>

              {/* Export & Import Buttons */}
              <button
                type="button"
                onClick={() => setIsExportModalOpen(true)}
                className="inline-flex items-center rounded-lg bg-white px-3 py-1.5 text-xs font-medium text-gray-700 border border-gray-300 hover:bg-gray-50 transition shadow-sm"
              >
                Export CSV
              </button>

              {hasImport && (
                <button
                  type="button"
                  onClick={() => setIsImportModalOpen(true)}
                  className="inline-flex items-center rounded-lg bg-white px-3 py-1.5 text-xs font-medium text-gray-700 border border-gray-300 hover:bg-gray-50 transition shadow-sm"
                >
                  Import CSV
                </button>
              )}
            </div>
          </div>

          {/* Expandable Multi-Condition Filter Builder */}
          {showFilterBuilder && (
            <div className="relative z-20 mt-3.5 pt-3.5 border-t border-gray-100 flex flex-wrap items-center gap-2">
              <SearchableDropdownOptions
                options={normalizedColumns
                  .filter((c) => c.showInFilter)
                  .map((c) => ({
                    value: c.label,
                    label: c.label,
                    field: c.field,
                    concat: c.concat || null,
                    subvalue: c.subvalue || null,
                    isDate: c.isDate || null,
                    relationWith: c.relationWith || null,
                    isRelation: c.isRelation || null,
                  }))}
                value={filterBy.name}
                onChange={async (e) => {
                  if (!e) {
                    setFilterBy({ name: "", value: "" });
                    setStatusOptions([]);
                    return;
                  }

                  const selectedValue =
                    window.location.pathname === "/vehicles" &&
                    e.value === "Category"
                      ? "class"
                      : window.location.pathname === "/vehiclerates" &&
                        e.value === "Vehicle Category"
                      ? "vehicle_class"
                      : e.field;

                  let filterUrl = `${config.apiUrl}/api/common/filtervalues?filterBy=${selectedValue}&tableNameInDb=${tableNameInDb}`;
                  let filterPath = selectedValue;

                  if (e.subvalue) {
                    filterPath = `${selectedValue.toLowerCase()}.${e.subvalue}`;
                    filterUrl += `&concat=${filterPath}`;
                  }

                  if (Array.isArray(e.concat) && e.concat.length > 0) {
                    filterPath = e.concat
                      .map((c) => `${c.field}.${c.subvalue}`)
                      .join(",");
                    filterUrl += `&concat=${filterPath}`;
                  }

                  setFilterBy({ name: e.value, value: filterPath });
                  if (e.isRelation === true && e.relationWith != null) {
                    filterUrl += `&relationWith=${e.relationWith}`;
                  }
                  setIsLoadingOptions(true);
                  try {
                    const response = await axios.get(filterUrl, {
                      headers: {
                        Authorization: `Bearer ${getCookie("_AT")}`,
                      },
                    });

                    syncAuthHeader(response.headers);

                    const items = Array.isArray(response?.data?.data)
                      ? response.data.data
                      : [];

                    const booleanLike =
                      items.length > 0 &&
                      items.every((v) => typeof v === "boolean");
                    let normalized = items;
                    if (booleanLike) {
                      const hasTrue = items.includes(true);
                      const hasFalse = items.includes(false);
                      if (!hasTrue || !hasFalse) {
                        normalized = [true, false];
                      }
                    } else {
                      const someBoolean = items.some(
                        (v) => typeof v === "boolean"
                      );
                      if (someBoolean && !items.includes(false)) {
                        normalized = [...items, false];
                      }
                    }

                    const seen = new Set();
                    const unique = normalized.filter((v) => {
                      const key = `${typeof v}:${String(v)}`;
                      if (seen.has(key)) return false;
                      seen.add(key);
                      return true;
                    });

                    const columnMeta = normalizedColumns.find(
                      (c) => c.label === e.value
                    );
                    const displayMap = columnMeta?.displayValues || null;

                    setStatusOptions(
                      unique.map((item) => {
                        if (typeof item === "boolean") {
                          const key = item ? "True" : "False";
                          const label = displayMap?.[key] || key;
                          return { value: item, label };
                        }
                        return { value: item, label: String(item) };
                      })
                    );
                    setFilterValue("");
                  } finally {
                    setIsLoadingOptions(false);
                  }
                }}
                placeholder="Filter By Column"
              />

              {filterBy.name &&
              normalizedColumns.find((c) => c.label === filterBy.name)
                ?.isDate ? (
                <>
                  <SearchableDropdownOptions
                    options={[
                      { label: "On or After", value: "_gte" },
                      { label: "On or Before", value: "_lte" },
                    ]}
                    value={dateComparisonOperator}
                    onChange={(e) => setDateComparisonOperator(e.value)}
                    placeholder="Comparison"
                  />
                  <div className="min-w-[200px]">
                    <InputDateTimePickerMUI
                      allowTime={false}
                      name="filter_date"
                      defaultValue={filterValue}
                      handleChange={(e) => setFilterValue(e.target.value)}
                      isClearable={true}
                      wrapperStyle={{ marginTop: 0 }}
                    />
                  </div>
                </>
              ) : (
                <SearchableDropdownOptions
                  options={statusOptions}
                  value={filterValue}
                  onChange={(e) =>
                    setFilterValue(
                      typeof e?.value === "boolean" ? e.value : e?.value
                    )
                  }
                  placeholder="Select Value"
                  disabled={!filterBy.name}
                  isLoading={isLoadingOptions}
                />
              )}

              <SearchableDropdownOptions
                options={[
                  { label: "AND", value: "_and" },
                  { label: "OR", value: "_or" },
                ]}
                value={filterOperator}
                onChange={(e) => setFilterOperator(e?.value || "_and")}
                placeholder="Operator"
              />

              <button
                type="button"
                onClick={async () => {
                  if (
                    filterBy.value != null &&
                    filterBy.value !== "" &&
                    filterValue !== "" &&
                    filterValue != null
                  ) {
                    const columnMeta = normalizedColumns.find(
                      (c) => c.label === filterBy.name
                    );
                    const isDate = columnMeta?.isDate;
                    const field = filterBy.value;
                    let value;
                    if (isDate) {
                      let dateForFilter = filterValue;
                      if (dateComparisonOperator === "_gte") {
                        const localDate = new Date(filterValue + "T00:00:00");
                        dateForFilter = localDate.toISOString();
                      } else if (dateComparisonOperator === "_lte") {
                        const localDate = new Date(filterValue + "T23:59:59");
                        dateForFilter = localDate.toISOString();
                      }
                      value = { [dateComparisonOperator]: dateForFilter };
                    } else {
                      value = filterValue;
                    }

                    const updatedFilters = [
                      ...activeFilters,
                      columnMeta?.isRelation === true &&
                      columnMeta?.relationSubvalue != null
                        ? {
                            field,
                            value,
                            subValue: columnMeta.relationSubvalue,
                            isRelation: columnMeta.isRelation,
                            operator: filterOperator || "_and",
                          }
                        : {
                            field,
                            value,
                            operator: filterOperator || "_and",
                          },
                    ];

                    setActiveFilters(updatedFilters);
                    await fetchData({
                      pageOverride: 1,
                      filtersOverride: updatedFilters,
                    });
                  }
                }}
                className="text-xs font-medium bg-blue-600 text-white px-3.5 py-2 rounded-lg hover:bg-blue-700 transition shadow-sm"
              >
                Apply Filter
              </button>
            </div>
          )}

          {/* Active Filter Chips Row */}
          {(activeFilters.length > 0 || searchTerm || sortedField) && (
            <div className="mt-3 pt-2.5 border-t border-gray-100 flex flex-wrap items-center justify-between gap-2">
              <div className="flex flex-wrap items-center gap-1.5">
                {activeFilters.map((f, index) => {
                  const findMatchingColumn = (fieldPath) => {
                    let column = normalizedColumns.find((c) => {
                      if (c.field === fieldPath) return true;
                      if (c.concat) {
                        const concatPath = c.concat
                          .map((col) =>
                            col.subvalue
                              ? `${col.field}.${col.subvalue}`
                              : col.field
                          )
                          .join(",");
                        return concatPath === fieldPath;
                      }
                      return false;
                    });
                    if (!column && fieldPath.includes(".")) {
                      const baseField = fieldPath.split(".")[0];
                      column = normalizedColumns.find(
                        (c) => c.field === baseField
                      );
                    }
                    return column;
                  };

                  const matchedCol = findMatchingColumn(f.field);
                  const fieldLabel = matchedCol?.label || f.field;

                  const getOperatorText = (operator) => {
                    const operatorMap = {
                      _gte: "On or After",
                      _lte: "On or Before",
                      _gt: "After",
                      _lt: "Before",
                      _eq: "Equals",
                      _neq: "Not Equals",
                      _contains: "Contains",
                      _in: "In",
                    };
                    return operatorMap[operator] || operator;
                  };

                  const formatChipValue = (value) => {
                    if (typeof value === "object" && !Array.isArray(value)) {
                      const operator = Object.keys(value)[0];
                      const val = Object.values(value)[0];
                      if (
                        typeof val === "string" &&
                        val.match(/^\d{4}-\d{2}-\d{2}T\d{2}:\d{2}:\d{2}/)
                      ) {
                        const date = new Date(val);
                        const formattedDate = `${date.getDate()}/${
                          date.getMonth() + 1
                        }/${date.getFullYear()}`;
                        return `${getOperatorText(operator)} ${formattedDate}`;
                      }
                      return `${getOperatorText(operator)} ${val}`;
                    }
                    if (typeof value === "boolean") {
                      const boolKey = value ? "True" : "False";
                      return matchedCol?.displayValues?.[boolKey] || boolKey;
                    }
                    const dropdownOption = statusOptions?.find(
                      (opt) => opt.value === value
                    );
                    if (dropdownOption) return dropdownOption.label;
                    return String(value);
                  };

                  return (
                    <span
                      key={`${f.field}-${index}`}
                      className="inline-flex items-center gap-1.5 rounded-full bg-blue-50 px-2.5 py-0.5 text-xs font-medium text-blue-700 ring-1 ring-inset ring-blue-700/10"
                    >
                      {index > 0 && (
                        <span className="text-[10px] font-bold uppercase text-blue-600">
                          {f.operator === "_or" ? "OR" : "AND"}
                        </span>
                      )}
                      <span>
                        {fieldLabel}: {formatChipValue(f.value)}
                      </span>
                      <button
                        type="button"
                        onClick={async () => {
                          const newFilters = activeFilters.filter(
                            (_, i) => i !== index
                          );
                          setActiveFilters(newFilters);
                          await fetchData({
                            pageOverride: 1,
                            filtersOverride: newFilters,
                          });
                        }}
                        className="text-blue-500 hover:text-blue-800"
                      >
                        <XMarkIcon className="h-3.5 w-3.5" />
                      </button>
                    </span>
                  );
                })}

                <button
                  type="button"
                  onClick={clearAllFiltersAndSearch}
                  className="text-xs font-semibold text-blue-600 hover:text-blue-800 ml-1"
                >
                  Clear All
                </button>
              </div>
            </div>
          )}
        </div>

        {/* Table Container using Dashboard scrollbar styling */}
        <div
          id="enhancedListContainer"
          style={{ maxHeight: divHeight ? `${divHeight}px` : "600px" }}
          className="dashboard-table-container overflow-x-auto overflow-y-auto relative z-10"
        >
          <table className="min-w-full divide-y divide-gray-200 text-sm border-separate border-spacing-0">
            <thead className="bg-gray-50 sticky top-0 z-20">
              <tr>
                {bulkActionsEnabled && (
                  <th
                    scope="col"
                    className={classNames(
                      densityConfig.headerPadding,
                      "w-10 text-left border-b border-gray-200 bg-gray-50 sticky left-0 z-30"
                    )}
                  >
                    <input
                      type="checkbox"
                      checked={
                        displayedData.length > 0 &&
                        displayedData.every((row) => isItemSelected(row.id))
                      }
                      onChange={toggleSelectAll}
                      className="h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300 rounded cursor-pointer"
                      title="Select page"
                    />
                  </th>
                )}

                {visibleColumns.map((column) => {
                  const colKey = column._colKey;
                  const customWidth = columnWidths[colKey];
                  const sorted = isColumnSorted(column);

                  return (
                    <th
                      key={colKey}
                      scope="col"
                      draggable
                      onDragStart={(e) => handleColumnDragStart(e, colKey)}
                      onDragOver={(e) => handleColumnDragOver(e, colKey)}
                      onDrop={(e) => handleColumnDrop(e, colKey)}
                      style={
                        customWidth
                          ? {
                              width: `${customWidth}px`,
                              minWidth: `${customWidth}px`,
                              maxWidth: `${customWidth}px`,
                            }
                          : undefined
                      }
                      className={classNames(
                        densityConfig.headerPadding,
                        "relative group text-left text-xs font-medium text-gray-500 uppercase tracking-wider border-b border-r border-gray-200/80 select-none transition-colors",
                        dragOverColKey === colKey
                          ? "bg-blue-50"
                          : "bg-gray-50 hover:bg-gray-100"
                      )}
                    >
                      <div className="flex items-center justify-between gap-1">
                        <button
                          type="button"
                          onClick={() => {
                            if (column.allowSorting === true) {
                              toggleSort(column);
                            }
                          }}
                          className={classNames(
                            "inline-flex items-center gap-1.5 truncate uppercase tracking-wider",
                            column.allowSorting
                              ? "cursor-pointer hover:text-gray-900"
                              : "cursor-default",
                            sorted
                              ? "text-gray-900 font-semibold"
                              : "text-gray-500 font-medium"
                          )}
                        >
                          <span className="truncate">{column.label}</span>
                          {column.allowSorting && (
                            <span
                              className={classNames(
                                "flex-none rounded transition-colors",
                                sorted
                                  ? "text-blue-600"
                                  : "text-gray-400 opacity-60 group-hover:opacity-100"
                              )}
                            >
                              {sorted ? (
                                sortDirection === "asc" ? (
                                  <ChevronUpIcon className="h-4 w-4" />
                                ) : (
                                  <ChevronDownIcon className="h-4 w-4" />
                                )
                              ) : (
                                <ChevronUpDownIcon className="h-4 w-4" />
                              )}
                            </span>
                          )}
                        </button>
                      </div>

                      <div
                        onMouseDown={(e) => handleResizeStart(e, colKey)}
                        onDoubleClick={(e) => resetColumnWidth(e, colKey)}
                        title="Drag to resize, double-click to reset"
                        className="group/resize absolute right-0 top-0 bottom-0 w-2.5 cursor-col-resize flex items-center justify-end z-10"
                      >
                        <span className="h-4 w-[2px] rounded-full bg-gray-300/90 group-hover:bg-gray-400 group-hover/resize:h-full group-hover/resize:w-1 group-hover/resize:bg-blue-500 active:bg-blue-600 transition-all" />
                      </div>
                    </th>
                  );
                })}

                <th
                  scope="col"
                  className={classNames(
                    densityConfig.headerPadding,
                    "border-b border-gray-200 bg-gray-50 text-right text-xs font-medium uppercase tracking-wider text-gray-500 sticky right-0 z-30 w-24"
                  )}
                >
                  Action
                </th>
              </tr>
            </thead>

            <tbody className="bg-white divide-y divide-gray-100">
              {displayedData.length > 0 ? (
                displayedData.map((row) => {
                  const rowCells = formattedCellCache.get(row.id) || {};
                  const selected =
                    bulkActionsEnabled && isItemSelected(row.id);
                  const canEdit = editRoles?.includes(currentRole);

                  return (
                    <tr
                      key={row.id}
                      onClick={(e) => {
                        if (e.target.type === "checkbox") return;
                        if (canEdit) {
                          navigate(`${row.id}`, {
                            state: { from: location },
                          });
                        }
                      }}
                      style={{
                        cursor: canEdit ? "pointer" : "not-allowed",
                      }}
                      className={classNames(
                        "group transition",
                        selected
                          ? "bg-blue-50/70 hover:bg-blue-50"
                          : "hover:bg-blue-50/40"
                      )}
                    >
                      {bulkActionsEnabled && (
                        <td
                          className={classNames(
                            densityConfig.cellPadding,
                            "w-10 sticky left-0 z-10 bg-white group-hover:bg-blue-50/40"
                          )}
                        >
                          <input
                            type="checkbox"
                            checked={selected}
                            onChange={(e) => {
                              e.stopPropagation();
                              toggleItemSelection(row.id, getItemName(row));
                            }}
                            onClick={(e) => e.stopPropagation()}
                            className="h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300 rounded cursor-pointer"
                          />
                        </td>
                      )}

                      {visibleColumns.map((column) => {
                        const colKey = column._colKey;
                        const cell = rowCells[colKey];
                        const customWidth = columnWidths[colKey];
                        const isIdCol = column.field === "id";
                        const isPlateCol =
                          column.field === "plate" ||
                          column.label?.toLowerCase() === "plate";
                        const isAmountCol = column.decimalCount === 2;
                        const isPrimaryNameCol =
                          column.label === "Name" ||
                          column.label === "Vehicle" ||
                          column.field === "reservation_code";

                        return (
                          <td
                            key={colKey}
                            style={
                              customWidth
                                ? {
                                    width: `${customWidth}px`,
                                    minWidth: `${customWidth}px`,
                                    maxWidth: `${customWidth}px`,
                                  }
                                : undefined
                            }
                            title={
                              cell?.truncated || customWidth
                                ? cell?.full
                                : undefined
                            }
                            className={classNames(
                              densityConfig.cellPadding,
                              densityConfig.textSize,
                              "whitespace-nowrap overflow-hidden text-ellipsis border-r border-gray-100"
                            )}
                          >
                            {cell?.kind === "status" && (
                              <span
                                className={classNames(
                                  "inline-flex items-center rounded-full font-medium ring-1 ring-inset",
                                  densityConfig.badgePadding,
                                  cell.textClass
                                )}
                              >
                                {cell.display}
                              </span>
                            )}

                            {cell?.kind === "color" && (
                              <div className="inline-flex items-center gap-2">
                                <span
                                  className="h-4 w-4 rounded-full ring-1 ring-black/10 flex-shrink-0"
                                  style={{ backgroundColor: cell.colorVal }}
                                />
                                <span className="text-gray-700">
                                  {cell.display}
                                </span>
                              </div>
                            )}

                            {(cell?.kind === "image_single" ||
                              cell?.kind === "image_concat") && (
                              <div className="flex items-center gap-3">
                                <img
                                  className="h-9 w-9 rounded-full object-cover flex-shrink-0 ring-1 ring-gray-200"
                                  src={cell.imageSrc}
                                  alt=""
                                />
                                <span className="truncate font-medium text-gray-900">
                                  {cell.display}
                                </span>
                              </div>
                            )}

                            {cell?.kind === "text" &&
                              (isIdCol ? (
                                <span className="font-semibold text-blue-600">
                                  #{cell.display}
                                </span>
                              ) : isPlateCol && cell.display ? (
                                <span className="font-mono text-xs px-2 py-0.5 bg-gray-100 rounded border border-gray-200 font-semibold text-gray-800">
                                  {cell.display}
                                </span>
                              ) : isAmountCol ? (
                                <span className="font-bold text-gray-900 tabular-nums">
                                  {cell.display}
                                </span>
                              ) : isPrimaryNameCol ? (
                                <span className="font-medium text-gray-900">
                                  {cell.display}
                                </span>
                              ) : (
                                <span className="text-gray-600">
                                  {cell.display}
                                </span>
                              ))}
                          </td>
                        );
                      })}

                      <td
                        className={classNames(
                          densityConfig.cellPadding,
                          "whitespace-nowrap text-right text-sm sticky right-0 z-10 bg-white/95 group-hover:bg-blue-50/60 backdrop-blur-sm border-l border-gray-100"
                        )}
                      >
                        <div className="inline-flex items-center justify-end gap-1.5">
                          <a
                            href={`${window.location.pathname}/${row.id}`}
                            target="_blank"
                            rel="noreferrer"
                            onClick={(e) => e.stopPropagation()}
                            title="Open in new tab"
                            className="p-1 rounded-md text-gray-400 hover:text-blue-600 hover:bg-blue-50 transition"
                          >
                            <svg
                              xmlns="http://www.w3.org/2000/svg"
                              width="18"
                              height="18"
                              viewBox="0 0 24 24"
                              fill="none"
                              stroke="currentColor"
                              strokeWidth="2"
                              strokeLinecap="round"
                              strokeLinejoin="round"
                            >
                              <path
                                stroke="none"
                                d="M0 0h24v24H0z"
                                fill="none"
                              />
                              <path d="M3 16m0 1a1 1 0 0 1 1 -1h3a1 1 0 0 1 1 1v3a1 1 0 0 1 -1 1h-3a1 1 0 0 1 -1 -1z" />
                              <path d="M4 12v-6a2 2 0 0 1 2 -2h12a2 2 0 0 1 2 2v12a2 2 0 0 1 -2 2h-6" />
                              <path d="M12 8h4v4" />
                              <path d="M16 8l-5 5" />
                            </svg>
                          </a>

                          {deleteRoles?.includes(currentRole) && (
                            <button
                              type="button"
                              name={row.id}
                              onClick={(e) => {
                                e.stopPropagation();
                                duplicateClicked(row.id);
                              }}
                              title="Duplicate record"
                              className="p-1 rounded-md text-gray-400 hover:text-gray-800 hover:bg-gray-100 transition"
                            >
                              <DocumentDuplicateIcon className="h-4 w-4" />
                            </button>
                          )}

                          {deleteRoles?.includes(currentRole) && (
                            <button
                              type="button"
                              name={row.id}
                              onClick={(e) => {
                                e.stopPropagation();
                                if (
                                  (process.env.REACT_APP_MANAGER_ROLE ===
                                    currentRole ||
                                    process.env.REACT_APP_FINANCE_ROLE ===
                                      currentRole) &&
                                  row.status !== "Draft" &&
                                  window.location.pathname.endsWith(
                                    "/reservations"
                                  )
                                )
                                  return;
                                deleteClicked(row.id);
                              }}
                              title="Delete record"
                              style={{
                                opacity:
                                  (process.env.REACT_APP_MANAGER_ROLE ===
                                    currentRole ||
                                    process.env.REACT_APP_FINANCE_ROLE ===
                                      currentRole) &&
                                  row.status !== "Draft" &&
                                  window.location.pathname.endsWith(
                                    "/reservations"
                                  )
                                    ? "40%"
                                    : "unset",
                                cursor:
                                  (process.env.REACT_APP_MANAGER_ROLE ===
                                    currentRole ||
                                    process.env.REACT_APP_FINANCE_ROLE ===
                                      currentRole) &&
                                  row.status !== "Draft" &&
                                  window.location.pathname.endsWith(
                                    "/reservations"
                                  )
                                    ? "not-allowed"
                                    : "pointer",
                              }}
                              className="p-1 rounded-md text-red-500 hover:text-red-700 hover:bg-red-50 transition"
                            >
                              <TrashIcon className="h-4 w-4" name={row.id} />
                              <span className="sr-only">Delete-{row.id}</span>
                            </button>
                          )}
                        </div>
                      </td>
                    </tr>
                  );
                })
              ) : (
                <tr>
                  <td
                    colSpan={
                      visibleColumns.length + (bulkActionsEnabled ? 2 : 1)
                    }
                    className="py-12 text-center text-sm text-gray-500"
                  >
                    No {entityName} found matching your current criteria.
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>

        {/* Footer Bar: Record Counter Pill & Pagination */}
        <div className="border-t border-gray-200 bg-gray-50/70 px-4 py-3 rounded-b-xl flex flex-col sm:flex-row items-center justify-between gap-2">
          <div className="flex items-center gap-2 text-xs text-gray-600 font-medium">
            <span className="px-2.5 py-0.5 text-xs font-semibold bg-blue-50 text-blue-700 ring-1 ring-inset ring-blue-700/10 rounded-full">
              {totalCount} Total
            </span>
            <span>
              Showing{" "}
              <span className="font-semibold text-gray-900">{firstRecord}</span>
              –<span className="font-semibold text-gray-900">{lastRecord}</span>{" "}
              of{" "}
              <span className="font-semibold text-gray-900">{totalCount}</span>{" "}
              {entityName}
            </span>
          </div>

          <PaginationButtons
            page={uiPage}
            totalCount={totalCount}
            pageSize={UI_PAGE_SIZE}
            nextButtonOnClick={async () =>
              await handleUiPageChange(uiPage + 1)
            }
            backButtonOnClick={async () =>
              await handleUiPageChange(uiPage - 1)
            }
          />
        </div>
      </div>

      <ExportCSVModal
        isOpen={isExportModalOpen}
        onClose={() => setIsExportModalOpen(false)}
        columns={exportableColumns}
        onExport={handleCSVExport}
      />

      {hasImport && (
        <ImportCSVModal
          isOpen={isImportModalOpen}
          onClose={() => setIsImportModalOpen(false)}
          columns={columns}
          tableName={tableNameInDb}
          relationshipMappings={importConfig?.relationshipMappings || []}
          onImportComplete={() => {
            setIsImportModalOpen(false);
            fetchData();
          }}
        />
      )}

      {showBulkActionPopup && bulkActionsEnabled && (
        <SequenceConfirmationPopup
          companies={selectedItems}
          onConfirm={handleBulkAction}
          onCancel={() => setShowBulkActionPopup(false)}
          sequencing={bulkActionProcessing}
          isBulkCompanyBilling={true}
        />
      )}
    </div>
  );
}
